import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { generateAsaResponse } from '@/lib/asaResponder';
import { Bot, Pill, BriefcaseBusiness, Send } from 'lucide-react';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

export default function LateAbsencePopup({ open, onClose, memberName }) {
  const [step, setStep] = useState(1); // 1: Initial, 2: Form Sakit, 3: Form Izin, 4: ASA Response
  const [formData, setFormData] = useState({});
  const [asaResponse, setAsaResponse] = useState('');

  const resetAndClose = () => {
    setStep(1);
    setFormData({});
    setAsaResponse('');
    onClose();
  };

  const set = (k, v) => setFormData(f => ({ ...f, [k]: v }));

  const handleSubmitSakit = () => {
    const res = generateAsaResponse('sakit', formData);
    setAsaResponse(res);
    setStep(4);
  };

  const handleSubmitIzin = () => {
    const res = generateAsaResponse('izin', formData);
    setAsaResponse(res);
    setStep(4);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && resetAndClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-none rounded-2xl shadow-2xl">
        {/* Header Style ASA */}
        <div className="bg-[#302621] p-5 text-white flex items-center gap-3">
          <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center border border-white/20 overflow-hidden">
            <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
          </div>
          <div>
            <h2 className="font-black text-lg">Asisten ASA</h2>
            <p className="text-xs text-[#E6D5B8]/80">HRD Virtual Satala Dermatech</p>
          </div>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto bg-slate-50">
          
          {step === 1 && (
            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-300">
              <div className="bg-white p-4 rounded-xl rounded-tl-none shadow-sm border border-slate-100 text-sm text-slate-700">
                <p>Halo <b>{memberName || 'Tim Satala'}</b>,</p>
                <p className="mt-2">Saya lihat jarum jam sudah melewati <b>08:05</b> dan belum ada rekam absensi kamu pagi ini.</p>
                <p className="mt-1">Apakah kamu sedang kurang sehat atau ada urusan mendesak yang memerlukan izin hari ini?</p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={() => setStep(2)}
                  className="flex-1 h-12 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold gap-2"
                >
                  <Pill className="w-4 h-4" /> Sakit
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2"
                >
                  <BriefcaseBusiness className="w-4 h-4" /> Izin
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
              <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl text-xs text-amber-800">
                Silakan isi data agar ASA bisa melaporkan status kesehatanmu ke manajemen.
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-700">Keluhan Sakit Apa?</label>
                  <Input placeholder="Cth: Demam tinggi dan flu berat" value={formData.keluhan || ''} onChange={(e) => set('keluhan', e.target.value)} className="bg-white mt-1" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700">Sudah Berobat / Minum Obat?</label>
                  <Select value={formData.sudahBerobat || ''} onValueChange={(v) => set('sudahBerobat', v)}>
                    <SelectTrigger className="bg-white mt-1"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sudah">Sudah</SelectItem>
                      <SelectItem value="belum">Belum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-700">Sakit Sejak</label>
                    <CustomDatePicker value={formData.sejak || ''} onChange={(e) => set('sejak', e.target.value)} className="bg-white mt-1" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-700">Estimasi Masuk</label>
                    <CustomDatePicker value={formData.estimasiSembuh || ''} onChange={(e) => set('estimasiSembuh', e.target.value)} className="bg-white mt-1" />
                  </div>
                </div>
                <Button 
                  disabled={!formData.keluhan || !formData.sudahBerobat || !formData.estimasiSembuh} 
                  onClick={handleSubmitSakit} 
                  className="w-full h-10 rounded-xl bg-[#302621] hover:bg-[#4A3B32] mt-4 font-bold"
                >
                  Kirim Konfirmasi Sakit
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
              <div className="bg-blue-50 border border-blue-100 p-3 rounded-xl text-xs text-blue-800">
                Silakan isi formulir izin. Jika izin lebih dari 2 hari, mohon komunikasikan juga dengan supervisor.
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-700">Kategori Izin</label>
                  <Input placeholder="Cth: Keperluan Keluarga, Administrasi, dll" value={formData.jenisIzin || ''} onChange={(e) => set('jenisIzin', e.target.value)} className="bg-white mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-700">Dari Tanggal</label>
                    <CustomDatePicker value={formData.dari || ''} onChange={(e) => set('dari', e.target.value)} className="bg-white mt-1" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-700">Sampai Tanggal</label>
                    <CustomDatePicker value={formData.sampai || ''} onChange={(e) => set('sampai', e.target.value)} className="bg-white mt-1" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700">Alasan Detail</label>
                  <Textarea placeholder="Berikan alasan yang jelas..." rows={3} value={formData.alasan || ''} onChange={(e) => set('alasan', e.target.value)} className="bg-white mt-1" />
                </div>
                <Button 
                  disabled={!formData.jenisIzin || !formData.dari || !formData.alasan} 
                  onClick={handleSubmitIzin} 
                  className="w-full h-10 rounded-xl bg-[#302621] hover:bg-[#4A3B32] mt-4 font-bold"
                >
                  Ajukan Izin
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-500">
              <div className="bg-white p-4 rounded-xl rounded-tl-none shadow-sm border border-slate-100 text-sm text-slate-700 relative">
                <div className="absolute -top-3 -left-3 w-6 h-6 bg-[#302621] rounded-full flex items-center justify-center">
                   <Bot className="w-3.5 h-3.5 text-[#E6D5B8]" />
                </div>
                <p className="whitespace-pre-wrap leading-relaxed">{asaResponse}</p>
              </div>
              <Button onClick={resetAndClose} variant="outline" className="w-full rounded-xl h-10 font-bold text-slate-500 hover:text-slate-700">
                Tutup Pesan
              </Button>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
